import { useEffect, useState } from 'react';
import { useFieldArray, FormProvider, useForm, useWatch } from 'react-hook-form';
import EnrollmentBookingFields from './EnrollmentBookingFields';
import { fetchPriceByType } from '../../../services/services';
import { writeCheckoutPrefill, serializeSchedulesForPrefill } from '../../../utils/checkoutPrefill';
import { useEnrollmentApiData } from '../../../hooks/useEnrollmentApiData';

function getInitialMeetKey(data, initialSelectedMeet) {
    const keys = Object.keys(data?.meetOptions || {});
    if (initialSelectedMeet && data?.meetOptions?.[initialSelectedMeet]) {
        return initialSelectedMeet;
    }
    return keys[0] || '';
}

function parseIdRupiahNumber(value) {
    if (value == null || value === '') return 0;
    const n = String(value).replace(/\./g, '').replace(/[^\d]/g, '');
    return Number(n) || 0;
}

function CourseInfoForm({
    data,
    initialSelectedMeet,
    typeCourse = '',
    cityId: cityIdProp = '',
    learningType: learningTypeProp = '',
    omitBasicsFields = false,
    selectedCourseTitle = '',
    onEnrollmentComplete,
    showPrice,
    meetItems,
    selectedItem,
    onSelectItem,
}) {

    const initialMeet = getInitialMeetKey(data, initialSelectedMeet);

    const methods = useForm({
        defaultValues: {
            meetPackage: initialMeet,
            classTypeCourse: '',
            cityId: '',
            learningType: '',
            programId: '',
            classMeet: '',
            classMeetWeekly: '',
            duration: '',
            schedules: [{ day: null, time: null }],
        },
        mode: 'onTouched',
    });

    const {
        control,
        setValue,
        watch,
        handleSubmit,
        formState: { errors },
    } = methods;

    const meetPackage = useWatch({ control, name: 'meetPackage' });
    const programId = useWatch({ control, name: 'programId' });
    const classMeet = useWatch({ control, name: 'classMeet' });
    const cityIdW = useWatch({ control, name: 'cityId' });
    const learningTypeW = useWatch({ control, name: 'learningType' });
    const classTypeCourse = useWatch({ control, name: 'classTypeCourse' });
    const effectiveTypeCourse = classTypeCourse || typeCourse || '';
    const [paymentApi, setPaymentApi] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const isTrial = effectiveTypeCourse === 't';
    const meetData = data?.meetOptions?.[meetPackage];

    const { cityList, learningTypeList, programList, classMeetList } = useEnrollmentApiData(
        cityIdW,
        learningTypeW,
        selectedItem?.id_program
    );

    const { fields } = useFieldArray({
        control,
        name: 'schedules',
    });

    const isScheduleComplete = () => {
        if (!fields.length) return false;

        return fields.every((_, index) => {
            const day = methods.getValues(`schedules.${index}.day`);
            const time = methods.getValues(`schedules.${index}.time`);
            return day && time;
        });
    };

    const isMainStepsComplete = () => {
        if (!classTypeCourse) return false;
        if (!cityIdW) return false;
        if (!learningTypeW) return false;

        if (effectiveTypeCourse === 'r' && !methods.getValues('classMeetWeekly')) {
            return false;
        }

        if (!methods.getValues('duration')) {
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (!selectedItem) return;

        setValue('programId', String(selectedItem.id_program));
        setValue('classMeet', selectedItem.id_type);
        setValue('meetPackage', selectedItem.meetKey);
    }, [selectedItem]);

    useEffect(() => {
        const complete = Boolean(
            classTypeCourse &&
            cityIdW &&
            learningTypeW
        );

        onEnrollmentComplete?.(complete);
    }, [classTypeCourse, cityIdW, learningTypeW]);

    useEffect(() => {
        if (effectiveTypeCourse !== 'r') {
            setPaymentApi(null);
            return;
        }
        const city = cityIdW || cityIdProp;
        const learn = learningTypeW || learningTypeProp;
        if (!programId || !classMeet || !city || !learn) {
            setPaymentApi(null);
            return;
        }
        let cancelled = false;
        fetchPriceByType({
            id_program: programId,
            id_type: classMeet,
            id_area: city,
            schedule: learn,
            non_member: 'N',
        })
            .then((res) => {
                if (!cancelled) setPaymentApi(res);
            })
            .catch(() => {
                if (!cancelled) setPaymentApi(null);
            });
        return () => {
            cancelled = true;
        };
    }, [effectiveTypeCourse, programId, classMeet, cityIdW, learningTypeW, cityIdProp, learningTypeProp]);

    const canShowPriceHeader =
        showPrice &&
        isMainStepsComplete() &&
        isScheduleComplete();

    const canShowTrialButton =
        isTrial &&
        watch('classTypeCourse') &&
        watch('cityId') &&
        watch('learningType');

    const isStepsComplete = Boolean(
        classTypeCourse &&
        cityIdW &&
        learningTypeW
    );

    const fallbackFix = parseIdRupiahNumber(meetData?.price);

    const displayFixPrice = isTrial
        ? 0
        : paymentApi?.fix_price != null
            ? paymentApi.fix_price
            : fallbackFix;

    const displayListPrice = isTrial
        ? null
        : paymentApi?.price != null
            ? paymentApi.price
            : parseIdRupiahNumber(meetData?.oldPrice) || null;

    const onBookCourse = handleSubmit((formValues) => {
        if (!isStepsComplete) {
            methods.setError('root', {
                type: 'manual',
                message: 'Selesaikan langkah 1-3 di kolom kiri terlebih dahulu.',
            });
            return;
        }

        setIsLoading(true);
        const paymentPayload = isTrial
            ? { price: 0, fix_price: 0 }
            : paymentApi
                ? paymentApi
                : { price: displayListPrice || fallbackFix, fix_price: displayFixPrice };

        const tp = formValues.classTypeCourse || typeCourse || 'r';
        const mp = formValues.meetPackage || '';
        const pn = selectedItem?.programName || '';
        const base = process.env.PUBLIC_URL || '';
        writeCheckoutPrefill({
            typeCourse: tp,
            cityId: formValues.cityId || cityIdProp,
            learningType: formValues.learningType || learningTypeProp,
            programId: formValues.programId,
            classMeet: formValues.classMeet || '',
            classMeetWeekly: formValues.classMeetWeekly || '',
            duration: formValues.duration,
            schedules: serializeSchedulesForPrefill(formValues.schedules),
            meetPackage: formValues.meetPackage,
            midtransPrice: displayFixPrice,
            payment: paymentPayload,
            courseJsonId: data?.id,
        });

        setTimeout(() => {
            window.location.href = `${base}/checkout?type=${tp}&program=${encodeURIComponent(pn)}&meet=${encodeURIComponent(mp)}`;
        }, 150);
    });

    if (!meetData) return null;

    const rupiah = new Intl.NumberFormat('id-ID');

    return (
        <FormProvider {...methods}>
            <div className="course-sidebar mt-5 mt-lg-0">
                <div className="course-widget course-details-info">
                    {!isStepsComplete && omitBasicsFields && (
                        <div className="alert alert-info small mb-3" role="status">
                            Ikuti langkah <strong>1–3</strong> pada kolom kiri. Setelah itu, pilih kartu program dan
                            lengkapi formulir di bawah.
                        </div>
                    )}

                    {errors.root && (
                        <div className="alert alert-warning small mb-3">{errors.root.message}</div>
                    )}

                    <form className="mb-3" onSubmit={(e) => e.preventDefault()}>
                        <EnrollmentBookingFields
                            showRegularMeetFields={effectiveTypeCourse === 'r'}
                            effectiveTypeCourse={effectiveTypeCourse}
                            omitBasicsFields={omitBasicsFields}
                            selectedCourseTitle={selectedCourseTitle}
                            cityList={cityList}
                            learningTypeList={learningTypeList}
                            programList={programList}
                            classMeetList={classMeetList}
                            meetItems={meetItems}
                            selectedItem={selectedItem}
                            onSelectItem={onSelectItem}
                            showPrice={showPrice}
                        />
                    </form>

                    <div className={`enrollment-transition ${canShowPriceHeader ? 'is-visible' : ''}`}>
                        {canShowTrialButton && (
                            <div className="buy-btn mt-3">
                                <button
                                    type="button"
                                    className="button button-enroll-course btn btn-main rounded"
                                    onClick={onBookCourse}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Proses...' : 'Daftar Trial Gratis'}
                                </button>
                            </div>
                        )}

                        {!isTrial && canShowPriceHeader && (
                            <div>
                                <div className="price-header">
                                    <h2 className="course-price pb-2">
                                        Rp. {rupiah.format(displayFixPrice)}
                                    </h2>
                                </div>

                                <div className="buy-btn mt-3">
                                    <button
                                        type="button"
                                        className="button button-enroll-course btn btn-main rounded"
                                        onClick={onBookCourse}
                                        disabled={isLoading}
                                    >
                                        Pesan Sekarang
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </FormProvider>
    );
}

const CourseInfo = ({
    data,
    initialSelectedMeet,
    selectedProgramFromCard,
    selectedItem,
    omitBasicsFields = false,
    selectedCourseTitle = '',
    onEnrollmentComplete,
    showPrice,
    meetItems,
    onSelectItem,
}) => {
    if (!data?.meetOptions || Object.keys(data.meetOptions).length === 0) {
        return null;
    }

    return (
        <CourseInfoForm
            data={data}
            initialSelectedMeet={initialSelectedMeet}
            selectedProgramFromCard={selectedProgramFromCard}
            selectedItem={selectedItem}
            omitBasicsFields={omitBasicsFields}
            selectedCourseTitle={selectedCourseTitle}
            onEnrollmentComplete={onEnrollmentComplete}
            showPrice={showPrice}
            meetItems={meetItems}
            onSelectItem={onSelectItem}
        />
    );
};

export default CourseInfo;